import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchPersonaGroups,
  createPersonaGroup,
  deletePersonaGroup,
  fetchPersonaGroupPersonas,
  assignPersonasToPersonaGroup,
  removePersonasFromPersonaGroup,
} from "../thunks/personaGroupsThunk.tsx";
import {
  personaGroupType,
  PersonaGroupsState as PersonaGroupsState,
} from "../types/personaGroupsTypes.tsx";

const initialState: PersonaGroupsState = {
  isLoading: false,
  personaGroups: null,
  selectedGeneration: null,
  selectedGenerationPersonas: null,
  isLoadingPersonas: false,
  isAssigning: false,
};

export const personaGroupsSlice = createSlice({
  name: "personaGroups",
  initialState,
  reducers: {
    setSelectedGeneration: (
      state,
      action: PayloadAction<personaGroupType | null>,
    ) => {
      state.selectedGeneration = action.payload;
    },
    clearGenerationPersonas: (state) => {
      state.selectedGenerationPersonas = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch persona groups
    builder.addCase(fetchPersonaGroups.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchPersonaGroups.fulfilled, (state, action) => {
      state.isLoading = false;
      state.personaGroups = action.payload;
    });
    builder.addCase(fetchPersonaGroups.rejected, (state) => {
      state.isLoading = false;
    });

    // Create persona group
    builder.addCase(createPersonaGroup.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createPersonaGroup.fulfilled, (state, action) => {
      state.isLoading = false;
      if (state.personaGroups) {
        state.personaGroups = [...state.personaGroups, action.payload];
      } else {
        state.personaGroups = [action.payload];
      }
    });
    builder.addCase(createPersonaGroup.rejected, (state) => {
      state.isLoading = false;
    });

    // Delete persona group
    builder.addCase(deletePersonaGroup.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(deletePersonaGroup.fulfilled, (state, action) => {
      state.isLoading = false;
      if (state.personaGroups) {
        state.personaGroups = state.personaGroups.filter(
          (g) => g.id !== action.payload,
        );
      }
    });
    builder.addCase(deletePersonaGroup.rejected, (state) => {
      state.isLoading = false;
    });

    // Fetch persona group personas
    builder.addCase(fetchPersonaGroupPersonas.pending, (state) => {
      state.isLoadingPersonas = true;
    });
    builder.addCase(fetchPersonaGroupPersonas.fulfilled, (state, action) => {
      state.isLoadingPersonas = false;
      state.selectedGenerationPersonas = action.payload;
    });
    builder.addCase(fetchPersonaGroupPersonas.rejected, (state) => {
      state.isLoadingPersonas = false;
    });

    // Assign personas to persona group
    builder.addCase(assignPersonasToPersonaGroup.pending, (state) => {
      state.isAssigning = true;
    });
    builder.addCase(assignPersonasToPersonaGroup.fulfilled, (state) => {
      state.isAssigning = false;
    });
    builder.addCase(assignPersonasToPersonaGroup.rejected, (state) => {
      state.isAssigning = false;
    });

    // Remove personas from persona group
    builder.addCase(removePersonasFromPersonaGroup.pending, (state) => {
      state.isAssigning = true;
    });
    builder.addCase(removePersonasFromPersonaGroup.fulfilled, (state) => {
      state.isAssigning = false;
    });
    builder.addCase(removePersonasFromPersonaGroup.rejected, (state) => {
      state.isAssigning = false;
    });
  },
});

export const { setSelectedGeneration, clearGenerationPersonas } =
  personaGroupsSlice.actions;
